import type { OrderStats } from '../../types/order'
import { formatEuros } from '../../lib/format'

// ─── Money-flow waterfall ──────────────────────────────────────────────────────
// Renders the server-derived bridge from what was charged (incl-VAT) down to the
// product margin (ex-VAT). Pure flex bars, scaled to the gross charged. All
// figures come from orderStats.

type WaterfallKind = 'base' | 'add' | 'subtract' | 'subtotal' | 'total'

interface WaterfallRow {
  label: string
  basis: string
  amount: number
  kind: WaterfallKind
  pct?: number | null
}

const WaterfallRowView = ({ row, scale }: { row: WaterfallRow; scale: number }) => {
  const width = scale > 0 ? Math.min((Math.abs(row.amount) / scale) * 100, 100) : 0
  const isDeduct = row.kind === 'subtract'
  const isTotal = row.kind === 'total'
  const negative = row.amount < 0
  const barColor = isDeduct
    ? 'bg-red-300'
    : row.kind === 'subtotal'
      ? 'bg-gray-300'
      : isTotal
        ? (negative ? 'bg-red-500' : 'bg-emerald-500')
        : row.kind === 'add'
          ? 'bg-primary-300'
          : 'bg-primary-400'
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className={`text-sm truncate ${isTotal || row.kind === 'subtotal' ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>
            {row.label}
          </span>
          <span className="text-[11px] text-gray-300 flex-shrink-0">{row.basis}</span>
        </div>
        <div className="flex items-baseline gap-2 flex-shrink-0 ml-3">
          <span className={`text-sm tabular-nums ${isDeduct ? 'text-red-500' : isTotal ? (negative ? 'font-bold text-red-600' : 'font-bold text-emerald-600') : 'font-semibold text-gray-900'}`}>
            {isDeduct ? `−${formatEuros(row.amount)}` : formatEuros(row.amount)}
          </span>
          {row.pct != null && (
            <span className="text-xs text-gray-400 w-12 text-right">{row.pct.toFixed(1)}%</span>
          )}
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  )
}

export const MarginWaterfall = ({ stats }: { stats: OrderStats }) => {
  const gross = stats.grossRevenueCents ?? 0
  const deliveryGross = stats.deliveryFeeCents ?? 0
  const packagingGross = stats.packagingFeeCents ?? 0
  const discount = stats.totalDiscountCents ?? 0
  // Everything charged that is SALES: goods plus both fees, after discounts.
  // Statiegeld is deliberately NOT in here: it carries no VAT, is not turnover
  // and is not margin, so folding it in would corrupt every line beneath AND
  // count a pass-through as a sale. It is added under the waterfall, where
  // "In till" reconciles to the payments report.
  const charged = gross + deliveryGross + packagingGross
  const collected = charged - discount
  const vat = stats.outputVatCents ?? 0
  // Collected − VAT == Turnover exactly (API identity on the totals).
  const turnover = stats.turnoverExVatCents ?? 0
  const deliveryFees = stats.deliveryFeeExVatCents ?? 0
  const productSales = stats.productSalesExVatCents ?? 0
  const commission = stats.platformCommissionCents ?? 0
  const net = stats.netRevenueCents ?? 0
  const cogs = stats.cogsCents ?? 0
  const margin = stats.contributionMarginCents ?? 0
  const deposit = stats.depositCents ?? 0
  const hasDelivery = deliveryFees > 0

  const rows: WaterfallRow[] = [
    { label: 'Gross sales', basis: charged > gross ? 'incl. VAT, goods' : 'incl. VAT', amount: gross, kind: 'base' },
    ...(deliveryGross > 0 ? [{ label: '+ Delivery fees', basis: 'incl. VAT', amount: deliveryGross, kind: 'add' as const }] : []),
    ...(packagingGross > 0 ? [{ label: '+ Verpakkingskosten', basis: 'incl. VAT', amount: packagingGross, kind: 'add' as const }] : []),
    { label: '− Discounts', basis: 'incl. VAT', amount: discount, kind: 'subtract' },
    { label: '= Collected', basis: 'incl. VAT', amount: collected, kind: 'subtotal' },
    { label: '− VAT', basis: 'output VAT', amount: vat, kind: 'subtract' },
    { label: '= Turnover', basis: 'ex. VAT', amount: turnover, kind: 'subtotal' },
    // Delivery is sales but never product: it leaves the chain here and gets
    // its own block below, so the margin answers "what do we make on the food".
    ...(hasDelivery
      ? [
          { label: '− Delivery fees', basis: 'ex. VAT, see below', amount: deliveryFees, kind: 'subtract' as const },
          { label: '= Product sales', basis: 'ex. VAT', amount: productSales, kind: 'subtotal' as const },
        ]
      : []),
    { label: '− Platform commission', basis: 'ex. VAT', amount: commission, kind: 'subtract' },
    { label: '= After commission', basis: 'ex. VAT', amount: net, kind: 'subtotal' },
    { label: '− COGS', basis: 'ex. VAT', amount: cogs, kind: 'subtract' },
    { label: '= Product margin', basis: 'ex. VAT, % of product sales', amount: margin, kind: 'total', pct: stats.contributionMarginPct },
  ]

  return (
    <div className="space-y-3">
      {rows.map((row) => (
        <WaterfallRowView key={row.label} row={row} scale={charged} />
      ))}

      {/* Delivery on its own. The commission on the fee is the SAME money already
          inside "− Platform commission" above, shown a second time against the
          fee — so this result is never added to the product margin. */}
      {hasDelivery && (
        <div className="pt-3 mt-1 border-t border-dashed border-gray-200 space-y-1">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-500">
              Delivery fees <span className="text-xs text-gray-400">(ex. VAT)</span>
            </span>
            <span className="text-sm tabular-nums text-gray-600">{formatEuros(deliveryFees)}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-500">
              − Commission on the fee <span className="text-xs text-gray-400">(part of platform commission)</span>
            </span>
            <span className="text-sm tabular-nums text-red-500">−{formatEuros(stats.deliveryFeeCommissionCents ?? 0)}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-gray-900">
              = Made on delivery <span className="text-xs font-normal text-gray-400">(not added to the product margin)</span>
            </span>
            <span className="text-sm tabular-nums font-semibold text-gray-900">
              {formatEuros(stats.deliveryResultCents ?? 0)}
            </span>
          </div>
        </div>
      )}

      {/* Statiegeld sits OUTSIDE the waterfall on purpose: it carries no VAT, is
          not turnover and is not margin — it is the customer's deposit passing
          through us on its way to the supplier we already paid it to. Shown
          here so the figures reconcile against the payments report, which sums
          what was actually charged. Hidden entirely when there is none. */}
      {deposit > 0 && (
        <div className="pt-3 mt-1 border-t border-dashed border-gray-200 space-y-1">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-500">
              + Statiegeld <span className="text-xs text-gray-400">(no VAT, pass-through)</span>
            </span>
            <span className="text-sm tabular-nums text-gray-600">{formatEuros(deposit)}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-semibold text-gray-900">
              = In till <span className="text-xs font-normal text-gray-400">(collected + statiegeld)</span>
            </span>
            <span className="text-sm tabular-nums font-semibold text-gray-900">
              {formatEuros(collected + deposit)}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
